import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as echarts from 'echarts';

const TempoVisualization = ({ activity, formatPace }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !activity.splits_metric) return;

    // Initialize chart
    chartInstance.current = echarts.init(chartRef.current);

    // Prepare data
    const splits = activity.splits_metric;
    const paceData = splits.map(split => ({
      value: split.moving_time,
      itemStyle: {
        color: getPaceColor(split.moving_time, splits)
      }
    }));

    const elevationData = splits.map(split => split.elevation_difference);

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        textStyle: {
          color: '#fff'
        },
        formatter: function(params) {
          const km = params[0].dataIndex + 1;
          const pace = formatPaceFromSeconds(params[0].value);
          const elevation = params[1] ? params[1].value.toFixed(1) : 0;
          return `
            <div style="padding: 8px;">
              <strong>Kilometer ${km}</strong><br/>
              Pace: ${pace}<br/>
              Elevation: ${elevation}m
            </div>
          `;
        }
      },
      grid: {
        left: '5%',
        right: '5%',
        top: '15%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: splits.map((_, index) => `${index + 1}km`),
        axisLine: {
          lineStyle: { color: 'rgba(255, 255, 255, 0.3)' }
        },
        axisLabel: {
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: 11
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Pace (min/km)',
          position: 'left',
          axisLine: {
            lineStyle: { color: 'rgba(255, 255, 255, 0.3)' }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 11,
            formatter: function(value) {
              return formatPaceFromSeconds(value);
            }
          },
          splitLine: {
            lineStyle: { color: 'rgba(255, 255, 255, 0.1)' }
          }
        },
        {
          type: 'value',
          name: 'Elevation (m)',
          position: 'right',
          axisLine: {
            lineStyle: { color: 'rgba(255, 255, 255, 0.3)' }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 11
          },
          splitLine: { show: false }
        }
      ],
      series: [
        {
          name: 'Pace',
          type: 'line',
          data: paceData,
          smooth: true,
          lineStyle: {
            width: 3,
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: '#ff6b35' },
                { offset: 0.5, color: '#f7931e' },
                { offset: 1, color: '#ff006e' }
              ]
            }
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(255, 107, 53, 0.3)' },
                { offset: 1, color: 'rgba(255, 107, 53, 0.05)' }
              ]
            }
          },
          symbol: 'circle',
          symbolSize: 6,
          emphasis: {
            symbolSize: 10
          }
        },
        {
          name: 'Elevation',
          type: 'bar',
          yAxisIndex: 1,
          data: elevationData,
          itemStyle: {
            color: 'rgba(59, 130, 246, 0.6)',
            borderRadius: [2, 2, 0, 0]
          },
          barWidth: '60%'
        }
      ],
      animation: true,
      animationDuration: 2000,
      animationEasing: 'cubicOut'
    };

    chartInstance.current.setOption(option);

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
    };
  }, [activity, formatPace]);

  const formatPaceFromSeconds = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getPaceColor = (pace, allPaces) => {
    const paces = allPaces.map(s => s.moving_time).sort((a, b) => a - b);
    const min = paces[0];
    const max = paces[paces.length - 1];
    const normalized = (pace - min) / (max - min);
    
    // Green for fast, red for slow
    const r = Math.floor(255 * normalized);
    const g = Math.floor(255 * (1 - normalized));
    return `rgb(${r}, ${g}, 50)`;
  };

  const generateArtisticVisualization = () => {
    if (!activity.splits_metric) return null;

    const splits = activity.splits_metric;
    const maxPace = Math.max(...splits.map(s => s.moving_time));
    const minPace = Math.min(...splits.map(s => s.moving_time));

    return (
      <div className="grid grid-cols-10 gap-1 mt-6 p-4 bg-black/20 rounded-xl">
        {splits.map((split, index) => {
          const intensity = (split.moving_time - minPace) / (maxPace - minPace);
          const height = 20 + (intensity * 60);
          const hue = 120 - (intensity * 120); // Green to Red
          
          return (
            <motion.div
              key={index}
              className="rounded-sm"
              style={{
                height: `${height}px`,
                backgroundColor: `hsl(${hue}, 70%, 50%)`,
                opacity: 0.8
              }}
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              whileHover={{ 
                scaleY: 1.2, 
                opacity: 1,
                transition: { duration: 0.2 }
              }}
              title={`Km ${index + 1}: ${formatPaceFromSeconds(split.moving_time)}`}
            />
          );
        })}
      </div>
    );
  };

  return (
    <motion.div
      className="h-full"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5 }}
    >
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white mb-2">{activity.name}</h3>
        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
          <span>Distance: {(activity.distance / 1000).toFixed(2)}km</span>
          <span>Avg Pace: {formatPace(activity.average_speed)}</span>
          <span>Elevation: {activity.total_elevation_gain}m</span>
        </div>
      </div>

      {/* Main Chart */}
      <div 
        ref={chartRef} 
        className="w-full h-64 mb-6"
        style={{ minHeight: '250px' }}
      />

      {/* Artistic Visualization */}
      <div>
        <h4 className="text-md font-medium text-white mb-3">Tempo Art</h4>
        {generateArtisticVisualization()}
        <p className="text-xs text-gray-500 mt-2 text-center">
          Each bar represents 1km - height and color indicate pace intensity
        </p>
      </div>
    </motion.div>
  );
};

export default TempoVisualization;